/**
 * «¿Por qué no puedo planificar esto?» — Diagnóstico SOLO LECTURA del estado interno de las líneas de una petición
 * (GET /admin/diag/rutabilidad). Mismas reglas que /lineas/disponibles-para-rutar. Admin / logística.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Stethoscope, Copy, Loader2, CheckCircle2, XCircle, Lock, MapPin, Route as RouteIcon, Package, Wrench, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export function DiagnosticoRutabilidadButton({ peticionId, lineaId, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)} data-testid="diagnostico-rutabilidad-btn"
        className={`text-zinc-400 hover:text-cyan-300 text-xs h-7 px-2 ${className}`} title="¿Por qué no puedo planificar esto? (solo lectura)">
        <Stethoscope className="w-3 h-3 mr-1" /> Diagnóstico
      </Button>
      {open && <DiagnosticoRutabilidadDialog open={open} onOpenChange={setOpen} peticionId={peticionId} lineaId={lineaId} />}
    </>
  );
}

export function DiagnosticoRutabilidadDialog({ open, onOpenChange, peticionId, lineaId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);
  const { hasRole } = useAuth();
  const esAdmin = hasRole("admin");
  useEffect(() => {
    if (!open) return;
    setErr(null);
    api.get("/admin/diag/rutabilidad", { params: lineaId ? { linea_id: lineaId } : { peticion_id: peticionId } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail?.msg || e?.response?.data?.detail || "No se pudo cargar el diagnóstico"));
  }, [open, peticionId, lineaId, tick]);
  const recargar = () => setTick((t) => t + 1);

  const copiar = () => {
    const txt = (data?.lineas || []).map((l) => [
      `Línea ${l.espacio?.nombre || l.descripcion || l.linea_id}${l.referencia_cliente ? ` (ref. ${l.referencia_cliente})` : ""}`,
      `  RUTABLE: ${l.rutable ? "SÍ" : "NO"} · estado línea: ${l.estado_linea} · petición: ${l.estado_peticion} · fecha: ${l.fecha_programada || "—"}`,
      `  ruta activa: ${l.ruta_activa ? `${l.ruta_activa.ruta_codigo} #${l.ruta_activa.parada_orden}` : "no"} · bloqueo_decision: ${l.bloqueo_decision.activo ? `SÍ (${l.bloqueo_decision.ruta_codigo} · ${l.bloqueo_decision.motivo})` : "no"}`,
      `  material: ${l.material.listos}/${l.material.total} ${l.material.estado}${l.material.ubicacion ? ` · ${l.material.ubicacion}` : ""} · vínculos comp→ruta: ${l.vinculos_componentes_ruta.length}`,
      ...l.motivos.map((m) => `  ✖ ${m.msg}`),
    ].join("\n")).join("\n\n");
    navigator.clipboard?.writeText(`Diagnóstico ${data?.peticion?.codigo || ""}\n${txt}`).then(() => toast.success("Diagnóstico copiado"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[85vh] overflow-y-auto" data-testid="diagnostico-rutabilidad-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Stethoscope className="w-4 h-4 text-cyan-400" /> Diagnóstico · ¿Por qué no puedo planificar esto?</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Solo lectura. Aplica las mismas reglas que «Disponible para rutar». {data?.peticion && <>Petición <span className="font-mono text-amber-300">{data.peticion.codigo}</span> · estado <b>{data.peticion.estado}</b> · {data.rutables}/{data.total} línea(s) rutable(s).</>}
          </DialogDescription>
        </DialogHeader>
        {err && <div className="text-sm text-rose-300" data-testid="diagnostico-error">{String(err)}</div>}
        {!data && !err && <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-cyan-400" /></div>}
        {data?.no_realizadas_pendientes?.length > 0 && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200" data-testid="diagnostico-nr-pendientes">
            <Lock className="w-3.5 h-3.5 inline mr-1" /> {data.no_realizadas_pendientes.length} parada(s) NO realizada(s) pendiente(s) de decisión:
            {data.no_realizadas_pendientes.map((n, i) => <span key={i} className="ml-1 font-mono">{n.ruta_codigo} #{n.parada_orden} ({n.motivo})</span>)}
            <Link to="/no-realizadas" className="ml-2 underline font-semibold">Resolver en No realizadas →</Link>
          </div>
        )}
        <div className="space-y-3">
          {(data?.lineas || []).map((l) => <LineaDiag key={l.linea_id} l={l} esAdmin={esAdmin} todas={data.lineas} onChanged={recargar} />)}
        </div>
        {data && (
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={copiar} data-testid="diagnostico-copiar" className="h-8 text-xs bg-zinc-900 border-zinc-700"><Copy className="w-3.5 h-3.5 mr-1" /> Copiar diagnóstico</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Dato({ k, v, warn }) {
  return <div className="flex gap-1 text-[11px]"><span className="text-zinc-500 shrink-0">{k}:</span><span className={warn ? "text-amber-300 font-semibold" : "text-zinc-200"}>{v}</span></div>;
}

function AccionesAdmin({ l, todas, onChanged }) {
  const [busy, setBusy] = useState(false);
  const bd = l.bloqueo_decision;
  const zonaMuerta = bd.activo && !l.ruta_activa;
  const originalesRutables = (todas || []).filter((x) => x.linea_id === l.clon_de && x.rutable);
  const recuperar = async () => {
    if (!window.confirm(`Recuperar esta línea: se eliminará SOLO el bloqueo residual de ${bd.ruta_codigo || "la ruta"} y quedará pendiente. Se guarda auditoría antes/después. ¿Continuar?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/lineas/${l.linea_id}/recuperar-zona-muerta`, { ruta_codigo: bd.ruta_codigo || "" });
      toast.success(r.data.rutable ? "Línea recuperada: ya es rutable" : "Línea recuperada, pero sigue sin ser rutable (ver motivos)");
      onChanged?.();
    } catch (e) { const d = e?.response?.data?.detail; toast.error(d?.msg || (typeof d === "string" ? d : "No se pudo recuperar")); }
    finally { setBusy(false); }
  };
  const eliminarClon = async (orig) => {
    if (!window.confirm(`Eliminar esta línea como CLON workaround de «${orig.espacio?.nombre || orig.linea_id}»? Solo se borra si no tiene rutas/albaranes/propuestas y la original es rutable. Se registra en el historial.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/lineas/${l.linea_id}/clon-workaround`, { data: { linea_original_id: orig.linea_id } });
      toast.success("Clon eliminado; historial conservado");
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.bloqueos?.map((b) => b.msg).join(" · ") || d?.msg || "No se pudo eliminar el clon", { duration: 9000 });
    } finally { setBusy(false); }
  };
  if (!zonaMuerta && !l.clon_de) return null;
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap border-t border-zinc-800 pt-2" data-testid={`diag-acciones-${l.linea_id}`}>
      <span className="text-[10px] uppercase tracking-wide text-zinc-500">Acciones admin</span>
      {zonaMuerta && (
        <Button size="sm" variant="outline" disabled={busy} onClick={recuperar} data-testid={`diag-recuperar-${l.linea_id}`}
          className="h-7 text-[11px] bg-emerald-500/10 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/20">
          <Wrench className="w-3 h-3 mr-1" /> Recuperar línea (quitar bloqueo residual)
        </Button>
      )}
      {l.clon_de && !originalesRutables.length && (
        <span className="text-[11px] text-amber-200" data-testid={`diag-clon-stop-${l.linea_id}`}>Clon de {l.clon_de.slice(0, 8)}… ({l.clon_info?.por || "—"}): la original aún no es rutable → recupérala primero (STOP).</span>
      )}
      {!l.ruta_activa && originalesRutables.map((o) => (
        <Button key={o.linea_id} size="sm" variant="outline" disabled={busy} onClick={() => eliminarClon(o)} data-testid={`diag-eliminar-clon-${l.linea_id}-${o.linea_id}`}
          className="h-7 text-[11px] bg-rose-500/10 border-rose-500/40 text-rose-200 hover:bg-rose-500/20">
          <Trash2 className="w-3 h-3 mr-1" /> Eliminar como clon de «{o.espacio?.nombre || o.linea_id.slice(0, 8)}»
        </Button>
      ))}
    </div>
  );
}

function LineaDiag({ l, esAdmin, todas, onChanged }) {
  const bd = l.bloqueo_decision;
  return (
    <div className={`rounded-lg border p-3 ${l.rutable ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`} data-testid={`diag-linea-${l.linea_id}`} data-rutable={l.rutable ? "1" : "0"}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        {l.rutable ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-amber-400" />}
        <span className="text-sm font-semibold"><MapPin className="w-3 h-3 inline text-zinc-500 mr-1" />{l.espacio?.nombre || l.descripcion || l.linea_id.slice(0, 8)}{l.espacio?.ciudad ? <span className="text-zinc-500 font-normal"> · {l.espacio.ciudad}</span> : null}</span>
        {l.referencia_cliente && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-200">Ref. {l.referencia_cliente}</span>}
        <span className={`ml-auto text-[10px] uppercase font-bold px-2 py-0.5 rounded ${l.rutable ? "bg-emerald-500/20 text-emerald-200" : "bg-amber-500/20 text-amber-200"}`} data-testid={`diag-rutable-${l.linea_id}`}>RUTABLE: {l.rutable ? "sí" : "no"}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
        <Dato k="Estado línea" v={l.estado_linea} />
        <Dato k="Estado petición" v={l.estado_peticion} />
        <Dato k="Fecha programada" v={l.fecha_programada || "—"} />
        <Dato k="Ruta activa" v={l.ruta_activa ? `${l.ruta_activa.ruta_codigo} #${l.ruta_activa.parada_orden} (${l.ruta_activa.ruta_estado})` : "no"} warn={!!l.ruta_activa} />
        <Dato k="agendada_en_ruta_id" v={l.agendada_en_ruta_id ? l.agendada_en_ruta_id.slice(0, 8) + "…" : "—"} warn={!!l.agendada_en_ruta_id && !l.ruta_activa} />
        <Dato k="Bloqueo por NO realizada" v={bd.activo ? `SÍ · ${bd.ruta_codigo || "ruta"} #${bd.parada_orden} · ${bd.motivo}${bd.detalle ? ` (${bd.detalle})` : ""}` : "no"} warn={bd.activo} />
        <Dato k="Material" v={<><Package className="w-3 h-3 inline mr-0.5" />{l.material.listos}/{l.material.total} · {l.material.estado}{l.material.ubicacion ? ` · ${l.material.ubicacion}` : ""}</>} />
        <Dato k="Vínculos componente→ruta" v={l.vinculos_componentes_ruta.length ? l.vinculos_componentes_ruta.map((v) => `${v.nombre || v.componente_id.slice(0, 6)} → ${v.parada_ruta_id?.slice(0, 8)} (${v.estado_ruta || "—"})`).join("; ") : "ninguno"} warn={!!l.vinculos_componentes_ruta.length && !l.ruta_activa} />
      </div>
      {l.motivos.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid={`diag-motivos-${l.linea_id}`}>
          {l.motivos.map((m, i) => (
            <li key={i} className="text-xs text-amber-200 flex gap-1.5" data-code={m.code}>
              <span>✖</span><span>{m.msg}{m.code === "bloqueo_decision" || m.ruta?.no_realizada_sin_decision ? <Link to="/no-realizadas" className="ml-1 underline">Resolver →</Link> : null}</span>
            </li>
          ))}
        </ul>
      )}
      {esAdmin && <AccionesAdmin l={l} todas={todas} onChanged={onChanged} />}
      {l.historial_rutas?.length > 0 && (
        <details className="mt-2 text-[11px] text-zinc-400">
          <summary className="cursor-pointer hover:text-zinc-200"><RouteIcon className="w-3 h-3 inline mr-1" />Historial de rutas ({l.historial_rutas.length})</summary>
          <ul className="mt-1 space-y-0.5 font-mono">
            {l.historial_rutas.map((h, i) => <li key={i}>{(h.at || "").slice(0, 16).replace("T", " ")} · {h.ruta_codigo} #{h.parada_orden} · {h.resultado}{h.motivo ? ` (${h.motivo})` : ""} → {h.decision || h.origen} · {h.por}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}
